import { GetStaticPropsResult, GetStaticPathsResult } from 'next'
import { ParsedUrlQuery } from 'querystring'
import { useContext } from 'react'

import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import {
  SanityPrintProductPage,
  SanityProductCatalogueQuery,
  SanityProductFragment,
} from '@data/sanity/queries/types/product'
import { getPreview } from '@lib/sanity/client'
import { getPrintProductPage, getProductCatalogue } from '@lib/sanity/product'
import { Locale } from '@lib/language'
import { getProductGalleryWithPrintAreas } from '@lib/print'
import { useProductWithInventory } from '@lib/product'
import {
  ProductVariantSelectionContext,
  ProductVariantSelectionContextProvider,
} from '@lib/product-variant-selection-context'
import {
  CustomStaticPropsContext,
  getPageUrl,
  PageType,
  PageUrlOptions,
} from '@lib/routes'
import { useSetPrintShop } from '@lib/shop'

import Layout from '@components/layout'
import PrintConfigurator from '@modules/shop/print-configurator'

interface StaticYearShopPrintConfiguratorPageParams extends ParsedUrlQuery {
  slug: string
  id: string
}

interface YearShopPrintConfiguratorPageProps {
  locale: Locale
  page: SanityPrintProductPage
  site: SanitySiteFragment
  productCatalogue: SanityProductCatalogueQuery
}

interface YearShopPrintConfiguratorPageContentProps {
  product: SanityProductFragment
  page: SanityPrintProductPage
  site: SanitySiteFragment
}

const YearShopPrintConfiguratorPageContent = ({
  product,
  page,
  site,
}: YearShopPrintConfiguratorPageContentProps) => {
  const {
    printShop,
    printPermissions,
    printConfiguratorStrings,
    printFeeSettings,
  } = page

  const { selectedVariant } = useContext(ProductVariantSelectionContext)

  useSetPrintShop(printShop)

  if (
    !selectedVariant ||
    !printShop ||
    !printPermissions?.yearShop ||
    !product.yearShop ||
    !printFeeSettings
  ) {
    return null
  }

  // Get product URL
  const urlOptions: PageUrlOptions = {
    parameters: {
      variant:
        product.surfaceOption && selectedVariant ? `${selectedVariant.id}` : '',
    },
  }
  const productUrl = getPageUrl(
    PageType.SHOP_PRODUCT,
    [printShop.type, printShop.id, product.slug],
    urlOptions
  )

  return (
    <Layout page={page} site={site}>
      <PrintConfigurator
        shopId={printShop.id}
        school={printShop.school}
        prints={printShop.prints ?? []}
        productType={product.type}
        printConfiguration={product.yearShop}
        printPermissions={printPermissions.yearShop}
        productGalleryWithPrintAreas={getProductGalleryWithPrintAreas(
          product.photos.main,
          product.yearShop
        )}
        productVariant={selectedVariant}
        productUrl={productUrl}
        printConfiguratorStrings={printConfiguratorStrings}
        printSlotFees={printFeeSettings.yearShop}
      />
    </Layout>
  )
}

/**
 * The product print page.
 */
const YearShopPrintConfiguratorPage = ({
  locale,
  page,
  site,
}: YearShopPrintConfiguratorPageProps) => {
  const product = useProductWithInventory(page, locale)

  return (
    <ProductVariantSelectionContextProvider
      product={product}
      useFallbackVariant={true}
    >
      <YearShopPrintConfiguratorPageContent
        product={product}
        page={page}
        site={site}
      />
    </ProductVariantSelectionContextProvider>
  )
}

export async function getStaticProps({
  locale,
  params,
  preview,
  previewData,
}: CustomStaticPropsContext<StaticYearShopPrintConfiguratorPageParams>): Promise<
  GetStaticPropsResult<YearShopPrintConfiguratorPageProps>
> {
  const productPage = await getPrintProductPage(
    locale,
    params?.id ?? '',
    params?.slug ?? '',
    getPreview(preview, previewData)
  )

  if (!productPage.page) {
    return { notFound: true }
  }

  const productCatalogue = await getProductCatalogue(locale)

  return {
    props: {
      locale,
      page: productPage.page,
      site: productPage.site,
      productCatalogue,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<StaticYearShopPrintConfiguratorPageParams>
> {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export default YearShopPrintConfiguratorPage
